// Where the visitor's choice stands, for the banner, the settings dialog and
// the runtime. Started once from main.tsx, before anything renders, so a stored
// choice is in force before any tracker could load.

import {
  CONSENT_STORAGE_KEY,
  NO_OPTIONAL,
  browserSendsGpc,
  honorGpc,
  readConsent,
  saveConsent,
  type ConsentChoices,
  type ConsentMethod,
  type ConsentRecord,
} from './consent';
import { applyConsent, clearTrackingCookies, startTracking } from './tracking';

/**
 * - `pending`: not started yet (the server render, and hydration). Nothing runs.
 * - `needs_choice`: nothing chosen. Nothing runs; the banner shows.
 * - `decided`: the visitor chose.
 */
export type ConsentStatus = 'pending' | 'needs_choice' | 'decided';

export interface ConsentSnapshot {
  status: ConsentStatus;
  /**
   * What is in force: the record, or nothing, with advertising off while the
   * browser sends Global Privacy Control.
   */
  choices: ConsentChoices;
  record: ConsentRecord | null;
  /** The browser sends Global Privacy Control. */
  gpc: boolean;
}

const PENDING: ConsentSnapshot = { status: 'pending', choices: NO_OPTIONAL, record: null, gpc: false };

let snapshot: ConsentSnapshot = PENDING;
/** Read once at start: the browser setting does not change under a page. */
let gpc = false;
const listeners = new Set<() => void>();

function publish(next: ConsentSnapshot) {
  snapshot = next;
  for (const listener of listeners) listener();
}

export function subscribeConsent(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getConsentSnapshot(): ConsentSnapshot {
  return snapshot;
}

/** What the server render and the hydration pass see: nothing decided yet. */
export function getServerConsentSnapshot(): ConsentSnapshot {
  return PENDING;
}

function applyRecord(record: ConsentRecord) {
  const choices = honorGpc(record, gpc);
  publish({ status: 'decided', choices, record, gpc });
  applyConsent(choices);
}

/**
 * Nothing chosen: nothing optional runs and the visitor is asked, wherever
 * they are. A browser sending Global Privacy Control is asked too, and keeps
 * advertising off whatever it answers.
 */
function askForChoice() {
  publish({ status: 'needs_choice', choices: NO_OPTIONAL, record: null, gpc });
  // Whatever an earlier, now expired or withdrawn choice left behind goes with it.
  clearTrackingCookies();
}

/** Another tab chose, or withdrew. */
function onStorage(event: StorageEvent) {
  if (event.key !== CONSENT_STORAGE_KEY) return;
  const record = readConsent();
  if (record) {
    applyRecord(record);
  } else if (snapshot.status === 'decided') {
    askForChoice();
  }
}

let started = false;

export function startConsent() {
  if (started) return;
  started = true;
  gpc = browserSendsGpc();
  startTracking();
  window.addEventListener('storage', onStorage);
  const record = readConsent();
  if (record) {
    applyRecord(record);
  } else {
    askForChoice();
  }
}

/** Keep the visitor's choice and act on it at once, withdrawal included. */
export function decideConsent(choices: ConsentChoices, method: ConsentMethod): ConsentRecord {
  const record = saveConsent(choices, method);
  applyRecord(record);
  return record;
}

const OPEN_EVENT = 'wc:privacy-settings';

/** Open the privacy settings from anywhere: the footer link, the banner, an admin page. */
export function openPrivacySettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenPrivacySettings(handler: () => void) {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

/** For tests: back to a page that has not started. */
export function resetConsentForTests() {
  started = false;
  snapshot = PENDING;
  gpc = false;
  listeners.clear();
  window.removeEventListener('storage', onStorage);
}
